import fm from 'format-message';
import PropTypes from 'prop-types';
import Link from '@activebrands/core-web/components/Link';
import Events from '@activebrands/core-web/libs/Events';
import { ProductEvents } from '@activebrands/core-web/libs/Events/types';
import { styled, useStyletron } from '@activebrands/core-web/libs/styletron';
import { productVariantLinkAccessibleName } from '@activebrands/core-web/utils/accessibility/product-variant-link-label';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
import RoundedImage from 'components/RoundedImage';

const Wrapper = styled('div', {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px',
});

const ColorSwatches = ({
    $style = {},
    currentVariationId = '',
    onColorHover,
    relatedVariations = [],
    selector,
    maxColors = 4,
}) => {
    const [css] = useStyletron();

    const trackHandler = id => {
        const selectedVariation = relatedVariations.find(variation => variation.id === id);

        if (!selectedVariation) {
            return;
        }

        const trackingObject = {
            id: selectedVariation.id || '',
            name: selectedVariation.name || '',
            sku: selectedVariation.sku || '',
            colorName: selectedVariation.colorName || '',
            colorHex: selectedVariation.colorHex || '',
            colorThumbnailUrl: selectedVariation.colorThumbnailUrl || '',
        };

        Events.trigger(ProductEvents.SELECT_COLOR, trackingObject);
    };

    return (
        <Wrapper
            $style={$style}
            data-selector={selector}
            aria-label={fm('Choose color')}
            role="group"
            onMouseLeave={() => onColorHover?.()}
        >
            {relatedVariations.slice(0, maxColors).map(variation => {
                const { colorHex, colorImageUrl, colorName, id, name, uri } = variation;

                return (
                    <Link
                        aria-label={productVariantLinkAccessibleName(name, colorName)}
                        className={css(getFocusRingStyles())}
                        key={uri}
                        to={uri}
                        onClick={trackHandler.bind(null, id)}
                        onMouseEnter={() => onColorHover?.(variation)}
                    >
                        <RoundedImage
                            $style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '100px',
                                backgroundColor: `#${colorHex}`,
                            }}
                            isSelected={id === currentVariationId}
                            name={name}
                            src={colorImageUrl ? { width: 14, url: colorImageUrl } : undefined}
                        />
                    </Link>
                );
            })}
            {relatedVariations.length > maxColors && (
                <span className={css({ marginLeft: '4px', display: 'block' })} onMouseEnter={() => onColorHover?.()}>
                    + {relatedVariations.length - maxColors}
                </span>
            )}
        </Wrapper>
    );
};

ColorSwatches.propTypes = {
    $style: PropTypes.object,
    currentVariationId: PropTypes.string,
    maxColors: PropTypes.number,
    onColorHover: PropTypes.func,
    relatedVariations: PropTypes.array,
    selector: PropTypes.string,
};

export default ColorSwatches;
